import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Music, Video, Palette, Sparkles, Check, ArrowRight, ArrowUpRight, MessageCircle, ExternalLink, Layers, Flame, Crown } from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import '../../styles/Home.css';
import '../../styles/Services.css';

const WA_LINK = "https://wa.me/916386568997";

const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#d9fb06" strokeWidth="2.5" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const influencers = [
  { name: 'little.anushka_', avgViews: '867K', followers: '1.3M', highestView: '26.8M', price: '₹45K' },
  { name: 'zeoneezi', avgViews: '368K', followers: '48.1K', highestView: '6M', price: '₹10K' },
  { name: 'jaslexxnn', avgViews: '225K', followers: '38.6K', highestView: '2.6M', price: '₹10K' },
  { name: 'nobonitasinghh', avgViews: '1.08M', followers: '153K', highestView: '19.4M', price: '₹22K' },
  { name: '_beingsne', avgViews: '1.2M', followers: '78.4K', highestView: '11.1M', price: '₹14K' },
  { name: 'iiamsaloni', avgViews: '645K', followers: '108K', highestView: '6M', price: '₹18K' },
  { name: 'saniyaofficial845', avgViews: '438K', followers: '35K', highestView: '13.9M', price: '₹15K' },
  { name: 'lavanya.das_', avgViews: '6M', followers: '1.7M', highestView: '143M', price: '₹1.7L' },
  { name: 'ankitaaaa_official', avgViews: '590K', followers: '427K', highestView: '150M', price: '₹39.5K' },
  { name: 'alishawwe_', avgViews: '251K', followers: '99.8K', highestView: '7.9M', price: '₹14K' },
  { name: '.ri.ti.ka.', avgViews: '406K', followers: '133K', highestView: '7M', price: '₹20K' },
  { name: '.snxha.', avgViews: '325K', followers: '172K', highestView: '23.2M', price: '₹19K' },
  { name: 'archaaana_', avgViews: '1.7M', followers: '650K', highestView: '108M', price: '₹98K' },
  { name: 'samriti_01', avgViews: '2.6M', followers: '430K', highestView: '59.7M', price: '₹65K' },
  { name: 'aishwaryaharishankar', avgViews: '1.06M', followers: '1.2M', highestView: '58.2M', price: '₹40K' },
];

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  const creativeServices = [
    {
      id: 'distribution',
      icon: <Radio size={32} strokeWidth={1.5} />,
      title: 'Global Music & Video\u00A0Distribution',
      desc: 'Deliver your songs and music videos to Spotify, Apple Music, YouTube Music, VEVO, JioSaavn, and 150+ DSPs in under 7\u00A0days.',
      features: [
        'Keep 100% of your copyright and master rights',
        'VEVO Music Video distribution included',
        'Dolby Atmos spatial audio delivery',
        'Official Artist Channel (OAC) setup on YouTube',
        'Monthly transparent UPI & bank payouts',
      ],
      badge: 'Core Service',
    },
    {
      id: 'release-management',
      icon: <Music size={32} strokeWidth={1.5} />,
      title: 'Release Management &\u00A0Metadata',
      desc: 'Optimize your catalog with ISRC/UPC generation, copyright management, delivery tracking, and DSP pitching\u00A0support.',
      features: [
        'Automated ISRC & UPC barcode provisioning',
        'Multi-artist split sheets and credits',
        'Official Artist Channel (OAC) setup',
        'Delivery status tracking and catalog protection',
      ],
      badge: 'Catalog',
    },
    {
      id: 'audio-engineering',
      icon: <Sparkles size={32} strokeWidth={1.5} />,
      title: 'Mix & Mastering Studio',
      desc: 'Get industry-grade audio polish with professional stem mixing, vocal tuning, dynamic mastering, and Dolby Atmos\u00A0mastering.',
      features: [
        'Industry loudness standards for streaming (-14 LUFS)',
        'Analog-modeled EQ, compression, and stereo imaging',
        'Vocal cleanup, pitch correction, and spatial effects',
        'Fast turnaround with multiple revision rounds',
      ],
      badge: 'Studio',
    },
    {
      id: 'visual-arts',
      icon: <Palette size={32} strokeWidth={1.5} />,
      title: 'Cover Art & Visual Design',
      desc: 'Eye-catching single/album artwork, 3D animated Spotify canvases, lyric banners, and social promotion kits.',
      features: [
        'DSP-compliant 3000x3000px high-resolution art',
        'Animated 9:16 vertical video canvases for Spotify',
        'Custom typography and brand identity assets',
        'Created by verified human graphic designers',
      ],
      badge: 'Visuals',
    },
    {
      id: 'video-production',
      icon: <Video size={32} strokeWidth={1.5} />,
      title: 'Music Video & Reel Editing',
      desc: 'High-energy visualizers, promo reels for Instagram/TikTok, and cinematic full-length music video post-production.',
      features: [
        'Dynamic rhythm-synced transitions and color grading',
        'Animated lyric videos with custom typography',
        'Short-form vertical video cutdowns for reels/shorts',
        'Fast delivery with creative revision support',
      ],
      badge: 'Video',
    },
    {
      id: 'artist-web',
      icon: <ExternalLink size={32} strokeWidth={1.5} />,
      title: 'Artist WEB Portfolios &\u00A0Discovery',
      desc: 'Your verified public portfolio connecting fans, curators, and collaborators directly to your music and contact\u00A0details.',
      features: [
        'Public shareable link (senritsux.in/artist/yourname)',
        'Embed Spotify, Apple Music, and YouTube releases',
        'Direct booking, collab inquiry, and social links',
        'Featured on SenritsuX Artist WEB',
      ],
      badge: 'Discovery',
    },
  ];

  return (
    <div className="senritsu-container">
      <SEO
        title="Services | SenritsuX - Music Distribution & Artist Services"
        description="Explore SenritsuX creator services: global music & video distribution to 150+ DSPs, Dolby Atmos, YouTube Content ID, VEVO setup, playlist pitching, and artist growth tools."
        canonical="/services"
        keywords={[
          'music distribution services',
          'independent artist services',
          'VEVO channel distribution',
          'Dolby Atmos music release',
          'YouTube Content ID monetization',
          'music influencer promotion',
        ]}
      />
      <Navbar />

      <main className="page-main-content">
        {/* Services Hero */}
        <section className="services-hero-section">
          <div className="services-hero-wrapper">
            <h1 className="services-hero-title">
              <span className="services-title-line">Complete Tools for</span>{' '}
              <span className="services-title-line title-highlight">Modern Music Creators</span>
            </h1>
            <p className="services-hero-subtitle">
              From global distribution and playlist placement to release management and visual design,
              <span className="brand-font"> SenritsuX</span> delivers end-to-end creative solutions tailored for independent artists.
            </p>
          </div>
        </section>

        {/* Scaled Services Content */}
        <div className="services-content-scaled">
          {/* 1. Distribution Plans Showcase */}
          <section className="services-dist-highlight">
          <div className="section-header-centered">
            <span className="service-number-pill">01</span>
            <h2 className="section-title">
              Music & Video&nbsp;<span className="title-highlight">Distribution</span>
            </h2>
            <p className="section-subtitle">
              Fast 7-day delivery to 150+ digital streaming platforms worldwide.
            </p>
          </div>

          <div className="dist-card-container">
            <div className="dist-card-modern">
              <div className="dist-card-details">
                <h3 className="dist-card-title">All-Platform Release Package</h3>
                <p className="dist-card-subtitle">
                  Release on Spotify, Apple Music, YouTube Music, Amazon, JioSaavn, Wynk, TikTok, and VEVO with zero takedown risk.
                </p>
                <div className="dist-features-grid">
                  {[
                    'YouTube Official Artist Channel (OAC)',
                    'YouTube Content ID Monetization',
                    'Dolby Atmos Spatial Audio Releases',
                    'VEVO Music Video Distribution',
                    'Instagram & Facebook Audio Library Sync',
                    '100% Copyright & Master Rights Retained',
                    'Direct UPI and Bank Account Payouts',
                    'No takedown unless requested by you',
                  ].map((item, idx) => (
                    <div key={idx} className="dist-feature-item">
                      <CheckIcon />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="service-action-row">
                  <Link
                    to="/distribution?mode=new"
                    className="cta-button"
                  >
                    Submit Your Track <ArrowUpRight size={15} style={{ marginLeft: 4, display: 'inline-block' }} />
                  </Link>
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary-glass"
                  >
                    <MessageCircle size={16} style={{ marginRight: 6 }} /> Contact on WhatsApp
                  </a>
                </div>
              </div>

              <div className="dist-pricing-column">
                <div className="dist-plan-box">
                  <span className="plan-tag">Flexible</span>
                  <span className="plan-label">Revenue Share</span>
                  <span className="plan-price">85%</span>
                  <span className="plan-note">Artist retains 85% · ₹0 Upfront Cost</span>
                </div>

                <div className="dist-plan-box featured-plan">
                  <span className="plan-tag popular">Most Popular</span>
                  <span className="plan-label">Lifetime Plan</span>
                  <span className="plan-price">₹1,599</span>
                  <span className="plan-note">97.5% Royalties Forever · One-time fee</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Playlist Pitching */}
        <section className="services-playlist-section">
          <div className="section-header-centered">
            <span className="service-number-pill">02</span>
            <h2 className="section-title">
              Curated Playlist&nbsp;<span className="title-highlight">Pitching</span>
            </h2>
            <p className="section-subtitle">
              Direct pitching campaigns to active, genre-targeted playlists to introduce your music to new curators. Streams are not guaranteed.
            </p>
          </div>

          <div className="playlist-tiers-container">
            <div className="playlist-historical-disclaimer" role="note" aria-label="Historical campaign results disclaimer">
              <span className="playlist-disclaimer-badge">Streams are not guaranteed.</span>
              <p className="playlist-disclaimer-text">
                <strong>IMPORTANT:</strong> The playlist, saves, saves-per-day, and average-stream figures displayed here are based on historical campaign results. They are provided for reference only and represent past outcomes, not guaranteed future performance. Actual results may vary. Playlist placement, saves, and streams are NOT guaranteed.
              </p>
            </div>

            <div className="playlist-tier-grid">
              <div className="playlist-tier-card tier-white">
                <span className="tier-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Layers size={14} /> White Tier
                </span>
                <div className="tier-price-tag">₹1,699</div>
                <div className="tier-stats-list">
                  <div className="tier-stat-row"><span>Playlists:</span> <strong>2 Playlists</strong></div>
                  <div className="tier-stat-row"><span>Total Saves:</span> <strong>10,000+</strong></div>
                  <div className="tier-stat-row"><span>Saves / Day:</span> <strong>9-12 saves</strong></div>
                  <div className="tier-stat-row"><span>Avg. Streams:</span> <strong>6K-8K</strong></div>
                </div>
                <div className="tier-disclaimer-note">Streams are not guaranteed.</div>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="tier-btn">
                  Select White Tier
                </a>
              </div>

              <div className="playlist-tier-card tier-black featured-tier">
                <span className="tier-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Flame size={14} color="#d9fb06" /> Black Tier (Popular)
                </span>
                <div className="tier-price-tag">₹6,200</div>
                <div className="tier-stats-list">
                  <div className="tier-stat-row"><span>Playlists:</span> <strong>6 Playlists</strong></div>
                  <div className="tier-stat-row"><span>Total Saves:</span> <strong>40,000+</strong></div>
                  <div className="tier-stat-row"><span>Saves / Day:</span> <strong>35-40 saves</strong></div>
                  <div className="tier-stat-row"><span>Avg. Streams:</span> <strong>25K-30K</strong></div>
                </div>
                <div className="tier-disclaimer-note">Streams are not guaranteed.</div>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="tier-btn tier-btn-featured">
                  Select Black Tier
                </a>
              </div>

              <div className="playlist-tier-card tier-diamond">
                <span className="tier-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Crown size={14} color="#d9fb06" /> Diamond Tier
                </span>
                <div className="tier-price-tag">₹12,400</div>
                <div className="tier-stats-list">
                  <div className="tier-stat-row"><span>Playlists:</span> <strong>12 Playlists</strong></div>
                  <div className="tier-stat-row"><span>Total Saves:</span> <strong>80,000+</strong></div>
                  <div className="tier-stat-row"><span>Saves / Day:</span> <strong>75-80 saves</strong></div>
                  <div className="tier-stat-row"><span>Avg. Streams:</span> <strong>55K-65K</strong></div>
                </div>
                <div className="tier-disclaimer-note">Streams are not guaranteed.</div>
                <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="tier-btn">
                  Select Diamond Tier
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Creative Marketplace & Studio Services */}
        <section className="services-creative-grid-section">
          <div className="section-header-centered">
            <span className="service-number-pill">03</span>
            <h2 className="section-title">
              Creator Marketplace &&nbsp;<span className="title-highlight">Studio Tools</span>
            </h2>
            <p className="section-subtitle">
              Connect directly with beatmakers, audio engineers, video creators, and graphic&nbsp;designers.
            </p>
          </div>

          <div className="creative-services-grid">
            {creativeServices.map((service) => (
              <div key={service.id} className="creative-service-card">
                <div className="service-card-top">
                  <div className="service-card-icon">{service.icon}</div>
                  <span className="service-card-badge">{service.badge}</span>
                </div>
                <h3 className="service-card-title">{service.title}</h3>
                <p className="service-card-desc">{service.desc}</p>
                <div className="service-card-features">
                  {service.features.map((feat, idx) => (
                    <div key={idx} className="card-feature-item">
                      <CheckIcon size={14} />
                      <span>
                        {feat.includes('SenritsuX') ? (
                          <>
                            {feat.split('SenritsuX')[0]}
                            <span className="brand-font">SenritsuX</span>
                            {feat.split('SenritsuX')[1]}
                          </>
                        ) : (
                          feat
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="service-card-footer">
                  <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="service-card-link">
                    Inquire on WhatsApp <ArrowRight size={14} style={{ marginLeft: 4, display: 'inline-block' }} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 4. Influencer Marketing Directory */}
        <section className="services-influencers-section">
          <div className="section-header-centered">
            <span className="service-number-pill">04</span>
            <h2 className="section-title">
              Influencer Marketing&nbsp;<span className="title-highlight">Network</span>
            </h2>
            <p className="section-subtitle">
              Promote your audio tracks across leading Instagram creators with verified view&nbsp;metrics.
            </p>
          </div>

          <div className="influencers-table-container">
            <div className="influencer-data-notice" role="note" aria-label="Sample data notice">
              ⚠️ Data Notice: This is outdated sample data, shown only to demonstrate how the influencer campaign feature works. It should not be treated as current influencer information or campaign availability.
            </div>

            <div className="influencers-grid-cards">
              {influencers.map((inf, idx) => (
                <div key={idx} className="influencer-card">
                  <div className="inf-header">
                    <span className="inf-handle">@{inf.name}</span>
                    <span className="inf-price">{inf.price}</span>
                  </div>
                  <div className="inf-metrics">
                    <div className="inf-metric-col">
                      <span className="inf-label">Followers</span>
                      <span className="inf-val">{inf.followers}</span>
                    </div>
                    <div className="inf-metric-col">
                      <span className="inf-label">Avg Views</span>
                      <span className="inf-val">{inf.avgViews}</span>
                    </div>
                    <div className="inf-metric-col">
                      <span className="inf-label">Peak View</span>
                      <span className="inf-val" style={{ color: '#d9fb06' }}>{inf.highestView}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="cta-button">
                Book Campaign on WhatsApp <ArrowUpRight size={15} style={{ marginLeft: 4, display: 'inline-block' }} />
              </a>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="about-cta-section">
          <div className="about-cta-card">
            <h2 className="about-cta-title">Need a Custom Music&nbsp;Package?</h2>
            <p className="about-cta-desc">
              Whether you need bundle pricing for an album or custom influencer distribution, our artist managers are ready to assist.
            </p>
            <div className="about-cta-btn-group">
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="cta-button">
                Chat with an Artist Manager
              </a>
            </div>
          </div>
        </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
